<template>
  <div class="change-tenant">
    <button
      type="button"
      class="change-tenant-button"
      :class="{
        'change-tenant-button--disabled': inquilinosAtivos.length === 0,
      }"
      :disabled="inquilinosAtivos.length === 0 || loading"
      :aria-label="$t('Acessar_empresa')"
      :title="text"
      @click.stop="abreModal($event)"
    >
      <span class="change-tenant-button__text">{{ text }}</span>
      <v-icon right size="18" v-if="inquilinosAtivos.length > 1">
        mdi-swap-horizontal
      </v-icon>
    </button>

    <v-dialog
      v-model="modal"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :max-width="dialogMaxWidth"
      scrollable
    >
      <v-card class="change-tenant-card loading-container">
        <div v-if="loading" class="loading-overlay" role="status">
          <div class="loading-card">
            <span class="spinner"></span>
            <span class="label">{{ $t("Carregando") }}</span>
          </div>
        </div>

        <v-card-title class="change-tenant-header">
          <div class="change-tenant-header__title">
            <span class="change-tenant-header__heading">
              {{ $t("Acessar_empresa") }}
            </span>
            <span class="change-tenant-header__current">{{ text }}</span>
          </div>
          <v-btn
            icon
            class="change-tenant-header__close"
            :disabled="loading"
            :aria-label="$t('Cancelar')"
            @click="modal = false"
          >
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
          <v-text-field
            ref="searchInput"
            v-model="procurar"
            :placeholder="$t('Procurar_empresa')"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            dense
            outlined
            autofocus
            class="change-tenant-search"
          />
        </v-card-title>

        <v-divider />

        <v-card-text class="change-tenant-list-container">
          <v-list class="change-tenant-list" two-line>
            <v-list-item-group active-class="black--text">
              <template v-for="(item, index) in inquilinosFiltrados">
                <v-list-item
                  :key="`tenant-${item.id}`"
                  class="change-tenant-item"
                  :class="{
                    'change-tenant-item--current': isCurrentTenant(item),
                  }"
                  :disabled="loading || isCurrentTenant(item)"
                  @click="changeTenant(item)"
                >
                  <v-list-item-avatar contain>
                    <v-img :src="item.inquilino.imagem" />
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title>
                      {{ tenantTitle(item) }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="tenantSubtitle(item)">
                      {{ tenantSubtitle(item) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-icon>
                    <v-icon v-if="isCurrentTenant(item)" color="success">
                      mdi-check-circle
                    </v-icon>
                    <v-icon v-else color="grey">mdi-login</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-divider
                  v-if="index < inquilinosFiltrados.length - 1"
                  :key="`divider-${item.id}`"
                />
              </template>

              <div
                v-if="!loading && inquilinosFiltrados.length === 0"
                class="empty-result"
              >
                {{ $t("Nenhum_resultado_encontrado") }}
              </div>
            </v-list-item-group>
          </v-list>
        </v-card-text>

        <v-card-actions class="change-tenant-actions">
          <v-spacer />
          <v-btn
            height="39"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
            "
            text
            :disabled="loading"
            @click="modal = false"
          >
            {{ $t("Cancelar") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ComumDataService from "@/services/ComumDataService";
import {
  descartarTrocaEmpresa,
  novoIdTrocaEmpresa,
  registrarTrocaEmpresa,
} from "@/utils/trocaEmpresaPush";

export default {
  name: "ChangeTenant",
  props: {
    text: { type: String, required: true },
  },

  data() {
    return {
      modal: false,
      procurar: "",
      loading: false,
    };
  },

  computed: {
    dialogMaxWidth() {
      return this.$vuetify.breakpoint.xsOnly ? undefined : 640;
    },

    currentTenantId() {
      return this.$store.state.inquilino?.id;
    },

    user() {
      return this.$store.state.user || {};
    },

    // Lista crua vinda do Vuex/localStorage
    inquilinos() {
      return this.user && this.user.inquilinos ? this.user.inquilinos : [];
    },

    // 🔒 Somente inquilinos com statusComum === 'ATIVO'
    inquilinosAtivos() {
      return this.inquilinos.filter((item) => {
        const status = item?.inquilino?.statusComum;
        return status === "ATIVO";
      });
    },

    // Busca pelo nome fantasia dentro apenas dos ATIVOS
    inquilinosFiltrados() {
      const termo = this.normalizaTexto(this.procurar);

      // se não tem texto de busca, retorna todos os ativos
      if (!termo) return this.inquilinosAtivos;

      // se tem busca, filtra
      return this.inquilinosAtivos.filter((item) => {
        const inquilino = item?.inquilino || {};
        const camposBusca = [
          inquilino.fantasia,
          inquilino.razaoSocial,
          inquilino.nRegistro,
          inquilino.nregistro,
        ];

        return camposBusca.some((campo) =>
          this.normalizaTexto(campo).includes(termo)
        );
      });
    },
  },

  methods: {
    normalizaTexto(valor) {
      return String(valor || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    },

    isCurrentTenant(item) {
      return item?.inquilino?.id === this.currentTenantId;
    },

    tenantTitle(item) {
      const inquilino = item?.inquilino || {};
      return inquilino.fantasia || inquilino.razaoSocial || this.$t("Sem_nome");
    },

    tenantSubtitle(item) {
      const inquilino = item?.inquilino || {};
      const razaoSocial = inquilino.razaoSocial;
      const fantasia = inquilino.fantasia;
      const nRegistro = inquilino.nRegistro || inquilino.nregistro;

      if (razaoSocial && razaoSocial !== fantasia) {
        return nRegistro ? `${razaoSocial} - ${nRegistro}` : razaoSocial;
      }

      return nRegistro || "";
    },

    getTenantId(item) {
      return item?.inquilino?.id || null;
    },

    focusSearch() {
      this.$nextTick(() => {
        window.setTimeout(() => {
          if (this.$refs.searchInput && this.$refs.searchInput.focus) {
            this.$refs.searchInput.focus();
          }
        }, 80);
      });
    },

    liberarFocoDoAcionador(event) {
      const target = event?.currentTarget || event?.target;
      if (target && typeof target.blur === "function") {
        target.blur();
      }

      if (typeof document === "undefined") {
        return;
      }

      const activeElement = document.activeElement;
      if (
        activeElement &&
        this.$el &&
        this.$el.contains(activeElement) &&
        typeof activeElement.blur === "function"
      ) {
        activeElement.blur();
      }
    },

    abreModal(event) {
      this.liberarFocoDoAcionador(event);

      // só abre modal se houver mais de um inquilino ativo
      if (this.inquilinosAtivos.length > 1) {
        this.procurar = "";
        this.modal = true;
      } else if (this.inquilinosAtivos.length === 1) {
        // se só tem um ativo, troca direto pra ele
        const unicoInquilino = this.inquilinosAtivos[0];
        if (!this.isCurrentTenant(unicoInquilino)) {
          this.changeTenant(unicoInquilino);
        }
      } else {
        // nenhum ativo (opcional: alerta)
        // this.$swal.fire(this.$t("Aviso"), this.$t("Nenhuma_empresa_disponivel"), "info");
      }
    },

    async changeTenant(item) {
      if (this.loading) return;

      let concluida = false;
      // Antes do PUT: o push da troca pode chegar antes da resposta.
      const trocaId = novoIdTrocaEmpresa();
      registrarTrocaEmpresa(trocaId);
      try {
        this.loading = true;
        const expectedTenantId = this.getTenantId(item);

        // 1) Chama o backend para alterar o tenant atual
        await ComumDataService.put(`usuarioInquilino/${item.id}`, item, {
          params: { trocaId },
        });

        // 2) Sinaliza a troca para telas em keep-alive fecharem modais locais.
        this.setTenantChangeInProgress(true);

        // 3) Atualiza o tenant no Vuex.
        await this.$store.dispatch("USER_REQUEST", {
          expectedTenantId,
          attempts: 6,
          delayMs: 300,
        });
        concluida = true;

        // A troca do ID do tenant já invalida o keep-alive do dashboard.
        // Não resete o cache aqui, pois isso remontaria a tela novamente.

        // 4) Sai do módulo atual com o tenant/cache já atualizados.
        if (this.$route.path !== "/dashboard") {
          await this.$router.push("/dashboard");
        }

        // 5) Fecha modal e avisa
        this.modal = false;

        this.$swal.fire(
          this.$t("Sucesso"),
          this.$t("Empresa_alterada_com_sucesso"),
          "success"
        );
      } catch (e) {
        console.error("changeTenant error", e);
        this.$swal.fire(
          this.$t("Erro"),
          this.$t("Falha_ao_alterar_empresa"),
          "error"
        );
      } finally {
        if (!concluida) descartarTrocaEmpresa(trocaId);
        this.setTenantChangeInProgress(false);
        this.loading = false;
        this.procurar = "";
      }
    },

    setTenantChangeInProgress(value) {
      if (typeof window === "undefined") return;
      window.__cajupeTenantChangeInProgress = !!value;
      window.dispatchEvent(
        new CustomEvent(
          value ? "cajupe:tenant-change-start" : "cajupe:tenant-change-end"
        )
      );
    },
  },

  watch: {
    modal(val) {
      if (val) {
        this.focusSearch();
      }
    },
  },
};
</script>

<style scoped>
.change-tenant {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.change-tenant-button {
  border: 1px solid #fff;
  border-radius: 8px;
  color: inherit;
  cursor: pointer;
  font-size: 1rem;
  gap: 8px;
  line-height: 1.2;
  min-width: 0;
  min-height: 38px;
  padding: 7px 10px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  transition: background-color 0.18s ease, border-color 0.18s ease;
}

.change-tenant-button:hover {
  background: rgba(255, 255, 255, 0.12);
}

.change-tenant-button--disabled {
  cursor: default;
  opacity: 0.55;
}

.change-tenant-button__text {
  display: -webkit-box;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.change-tenant-card {
  max-height: 86vh;
}

.change-tenant-header {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  padding: 18px 20px 14px;
}

.change-tenant-header__title {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.change-tenant-header__heading {
  color: #344767;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.35;
}

.change-tenant-header__current {
  color: #67748e;
  display: block;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.35;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.change-tenant-header__close {
  flex: 0 0 auto;
  margin-top: -4px;
}

.change-tenant-search {
  flex: 0 0 100%;
  margin-top: 2px;
  width: 100%;
}

.change-tenant-list-container {
  padding: 8px 12px !important;
}

.change-tenant-list {
  padding: 0;
}

.change-tenant-item {
  border-radius: 8px;
  margin: 3px 0;
  min-height: 58px;
}

.change-tenant-item--current {
  background: rgba(76, 175, 80, 0.08);
}

.change-tenant-actions {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 12px 16px;
}

.empty-result {
  padding: 24px 16px;
  color: #777;
  font-size: 0.9rem;
  text-align: center;
}

p {
  margin: 0;
}

@media (max-width: 600px) {
  .change-tenant-card {
    border-radius: 0 !important;
    height: 100%;
    max-height: 100vh;
  }

  .change-tenant-header {
    padding: 16px 16px 12px;
  }

  .change-tenant-list-container {
    padding: 8px !important;
  }

  .change-tenant-actions {
    padding: 10px 12px;
  }
}
</style>
